import { useLayoutEffect, useRef, useState, FormEvent } from 'react';
import { gsap, revealUp } from '../lib/motion';
import { submitLead } from '../lib/leads';

interface Fields {
  company: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  size: string;
  description: string;
  /** Honeypot — hidden from real users, must stay empty. */
  website: string;
}

const EMPTY: Fields = {
  company: '',
  name: '',
  email: '',
  phone: '',
  location: '',
  size: '',
  description: '',
  website: '',
};

const SIZES = [
  'Single unit / small repair',
  '1–25 units',
  '25–100 units',
  '100+ units / full property',
  'Commercial build-out',
  'Roofing program',
];

type Status = 'idle' | 'sending' | 'sent' | 'error';

export default function Contact() {
  const ref = useRef<HTMLElement>(null);
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [status, setStatus] = useState<Status>('idle');

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      revealUp('.ct-reveal', el);
    }, el);
    return () => ctx.revert();
  }, []);

  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFields((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = (): boolean => {
    const er: Partial<Record<keyof Fields, string>> = {};
    if (!fields.name.trim()) er.name = 'Contact name is required.';
    if (!fields.email.trim()) er.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) er.email = 'Enter a valid email address.';
    if (fields.phone.trim() && !/^[\d\s()+.-]{7,}$/.test(fields.phone)) er.phone = 'Enter a valid phone number.';
    if (!fields.location.trim()) er.location = 'Project location is required.';
    if (!fields.description.trim()) er.description = 'Tell us a little about the project.';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const mailtoFallback = () => {
    const body = [
      `Company: ${fields.company || '—'}`,
      `Contact: ${fields.name}`,
      `Email: ${fields.email}`,
      `Phone: ${fields.phone || '—'}`,
      `Project Location: ${fields.location}`,
      `Estimated Size: ${fields.size || '—'}`,
      '',
      'Project Description:',
      fields.description,
    ].join('\n');
    window.location.href = `mailto:mrocha@revivebuildersllc.com?subject=${encodeURIComponent(
      `Bid Request — ${fields.company || fields.name}`
    )}&body=${encodeURIComponent(body)}`;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate() || status === 'sending') return;

    setStatus('sending');
    const ok = await submitLead({
      name: fields.name,
      email: fields.email,
      phone: fields.phone || undefined,
      company: fields.company || undefined,
      location: fields.location || undefined,
      projectSize: fields.size || undefined,
      description: fields.description || undefined,
      website: fields.website,
    });

    if (ok) {
      setStatus('sent');
      setFields(EMPTY);
    } else {
      // Direct submission failed — fall back to the visitor's email client
      setStatus('error');
      mailtoFallback();
    }
  };

  const err = (k: keyof Fields) =>
    errors[k] ? <p className="mt-1.5 text-xs text-red-400">{errors[k]}</p> : null;

  return (
    <section ref={ref} id="contact" className="relative overflow-hidden bg-ink-950 py-28 md:py-40">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: 'url("/images/noir-marble.jpg")' }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-950 via-ink-950/60 to-ink-950" />

      <div className="relative mx-auto max-w-4xl px-6 md:px-10">
        <div className="ct-reveal mb-14 text-center">
          <p className="eyebrow mb-6">Contact</p>
          <h2 className="font-display text-5xl font-medium leading-tight text-steel-100 md:text-6xl">
            Request <span className="italic text-gold-300">a bid.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base font-light text-steel-300">
            Tell us about the property. We'll walk it, scope it, and get you a number you can build a budget around.
          </p>
        </div>

        {status === 'sent' ? (
          <div className="ct-reveal glass rounded-sm p-10 text-center md:p-16">
            <p className="font-display text-3xl font-medium text-steel-100 md:text-4xl">
              Request <span className="italic text-gold-300">received.</span>
            </p>
            <p className="mx-auto mt-5 max-w-md text-base font-light leading-relaxed text-steel-300">
              We'll reach out within one business day to schedule a walk-through. Need us sooner?
            </p>
            <a
              href="tel:+12103931759"
              className="mt-8 inline-block border border-gold-500/40 px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-gold-300 transition-all duration-300 hover:bg-gold-500 hover:text-ink-950"
            >
              Call (210) 393-1759
            </a>
          </div>
        ) : (
        <form onSubmit={onSubmit} noValidate className="ct-reveal glass rounded-sm p-7 md:p-12">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="company" className="mb-2 block text-xs uppercase tracking-widest2 text-steel-400">Company Name</label>
              <input id="company" className="field" value={fields.company} onChange={set('company')} placeholder="Acme Property Group" autoComplete="organization" />
            </div>
            <div>
              <label htmlFor="name" className="mb-2 block text-xs uppercase tracking-widest2 text-steel-400">Contact Name *</label>
              <input id="name" className={`field ${errors.name ? 'invalid' : ''}`} value={fields.name} onChange={set('name')} placeholder="Jane Smith" autoComplete="name" required />
              {err('name')}
            </div>
            <div>
              <label htmlFor="email" className="mb-2 block text-xs uppercase tracking-widest2 text-steel-400">Email *</label>
              <input id="email" type="email" className={`field ${errors.email ? 'invalid' : ''}`} value={fields.email} onChange={set('email')} placeholder="jane@company.com" autoComplete="email" required />
              {err('email')}
            </div>
            <div>
              <label htmlFor="phone" className="mb-2 block text-xs uppercase tracking-widest2 text-steel-400">Phone</label>
              <input id="phone" type="tel" className={`field ${errors.phone ? 'invalid' : ''}`} value={fields.phone} onChange={set('phone')} placeholder="(210) 555-0100" autoComplete="tel" />
              {err('phone')}
            </div>
            <div>
              <label htmlFor="location" className="mb-2 block text-xs uppercase tracking-widest2 text-steel-400">Project Location *</label>
              <input id="location" className={`field ${errors.location ? 'invalid' : ''}`} value={fields.location} onChange={set('location')} placeholder="San Antonio, TX" required />
              {err('location')}
            </div>
            <div>
              <label htmlFor="size" className="mb-2 block text-xs uppercase tracking-widest2 text-steel-400">Estimated Project Size</label>
              <select id="size" className="field appearance-none" value={fields.size} onChange={set('size')}>
                <option value="">Select a range…</option>
                {SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="description" className="mb-2 block text-xs uppercase tracking-widest2 text-steel-400">Project Description *</label>
              <textarea id="description" rows={5} className={`field resize-none ${errors.description ? 'invalid' : ''}`} value={fields.description} onChange={set('description')} placeholder="Scope, timeline, and anything else we should know." required />
              {err('description')}
            </div>
            {/* Honeypot field — visually hidden, bots fill it, humans never see it */}
            <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input id="website" tabIndex={-1} autoComplete="off" value={fields.website} onChange={set('website')} />
            </div>
          </div>

          <button
            type="submit"
            disabled={status === 'sending'}
            className="mt-9 w-full bg-gold-500 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-ink-950 transition-all duration-500 hover:bg-gold-400 hover:shadow-[0_0_50px_rgba(201,118,47,0.35)] disabled:cursor-wait disabled:opacity-70"
          >
            {status === 'sending' ? 'Sending…' : 'Request a Bid'}
          </button>
          {status === 'error' && (
            <p className="mt-4 text-center text-xs text-red-400">
              Direct submission didn't go through — we opened your email client instead, or call us below.
            </p>
          )}
          <p className="mt-5 text-center text-xs text-steel-500">
            Or call us directly at{' '}
            <a href="tel:+12103931759" className="text-steel-300 underline-offset-4 hover:text-gold-300 hover:underline">
              (210) 393-1759
            </a>
          </p>
        </form>
        )}
      </div>
    </section>
  );
}
